import { SyntheticEvent } from 'react'
import { CellContext } from '@tanstack/react-table'

import { Log, LogSource, MandatoryLogInformation } from '@/types/logs'
import { tableCellStyles } from '@/ui/Table'
import { useAuthStore } from '@/store/auth-store'
import { MissingInfo } from './MissingInfo'
import { Button } from '@/ui/Button'
import { Role } from '@/types/auth'
import { ModalTypes, useModal } from '@/components/Modal'
import { getOfficialReportTypeByCode } from '@/modules/CombinedLogs/helpers/getOfficialReportTypeByCode'
import { OfficialReportStatus } from '@/types/officialReport'
import { useCombinedLogsTableStateStore } from '@/modules/CombinedLogs/store'

import styles from './Description.module.scss'

import ChevronRight from '../../../assets/chevron-right.svg'
import clsx from 'clsx'

const OfficialReport = ({ log }: { log: Log }) => {
    const { role } = useAuthStore()
    const { openModal } = useModal()

    const isReportAvailable = !!log.report && log.report.status !== OfficialReportStatus.Draft
    const isDraftAvailable = log.report && log.report.status === OfficialReportStatus.Draft
    const isReportRequired = log.codeTypeReasonInfo?.isOfficialReportRequired
    const reportType = getOfficialReportTypeByCode(log)
    const editable = log.codingPeriodInfo.permissions.includes('Update') && !log.isLocked

    const openReport = (isEditable: boolean) => (event: SyntheticEvent) => {
        event.stopPropagation()

        openModal(ModalTypes.officialReport, {
            type: reportType,
            log,
            readOnly: !isEditable,
        })
    }

    if (role !== Role.MileageClerk) {
        return null
    }

    if (isReportRequired && !isReportAvailable) {
        return (
            <Button
                className={clsx(styles.missing, styles.action)}
                size="small"
                variant="transparent"
                onClick={openReport(editable)}
                disabled={!editable}
            >
                {isDraftAvailable ? 'Update' : 'Add'} official report <ChevronRight />
            </Button>
        )
    }

    if (!isReportAvailable) {
        return null
    }

    return (
        <Button className={styles.action} size="small" variant="transparent" onClick={openReport(false)}>
            View official report <ChevronRight />
        </Button>
    )
}

const MechanicalInformation = ({ log }: { log: Log }) => {
    const { role } = useAuthStore()
    const { openModal } = useModal()

    const isInfoAvailable = !!log.mechanicalInfo
    const isInfoRequired = log.codeTypeReasonInfo?.isMechanicalInfoRequired

    const editable =
        log.source === LogSource.Manual && log.codingPeriodInfo.permissions.includes('Update') && !log.isLocked

    const viewAddInfo = (event: SyntheticEvent) => {
        event.stopPropagation()

        openModal(ModalTypes.mechanicalInformation, {
            log,
            readOnly: !editable,
        })
    }

    if (role !== Role.MileageClerk) {
        return null
    }

    if (isInfoRequired && !isInfoAvailable) {
        return (
            <Button
                className={clsx(styles.missing, styles.action)}
                size="small"
                variant="transparent"
                onClick={viewAddInfo}
                disabled={!editable}
            >
                Add mechanical information <ChevronRight />
            </Button>
        )
    }

    if (!isInfoAvailable) {
        return null
    }

    return (
        <Button className={styles.action} size="small" variant="transparent" onClick={viewAddInfo}>
            View mechanical information <ChevronRight />
        </Button>
    )
}

export const Description = ({ getValue, row }: CellContext<Log, Log['descriptionInfo']>) => {
    const { multipleSelection } = useCombinedLogsTableStateStore()
    const value = getValue()
    const isRowSelected = multipleSelection.find((item) => item.id === row.original.id)
    const isDescriptionExist = value.description || value.extraInfo || value.strategy

    return (
        <>
            <div className={styles.container}>
                {isDescriptionExist && (
                    <>
                        <p>{value.description}</p>
                        {isRowSelected && (
                            <>
                                <p className={tableCellStyles.greyCell}>{value.extraInfo}</p>
                                <p className={tableCellStyles.greyCell}>{value.strategy}</p>
                            </>
                        )}
                    </>
                )}
                {!isDescriptionExist && (
                    <MissingInfo label="description" log={row.original} field={MandatoryLogInformation.Description} />
                )}
            </div>
            <OfficialReport log={row.original} />
            <MechanicalInformation log={row.original} />
        </>
    )
}
